import { Box, Divider, Typography, Button, Paper } from '@mui/material';
import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { ReactComponent as SuccessIcon } from 'src/assets/svg/success-green-circle.svg';
import { ReactComponent as TransferMateIcon } from 'src/assets/svg/transfer-mate.svg';
import { ReactComponent as VisaIcon } from 'src/assets/svg/visa.svg';
import { ReactComponent as AmericanExpressIcon } from 'src/assets/svg/american-express.svg';
import { ReactComponent as MastercardIcon } from 'src/assets/svg/mastercard.svg';
import { ReactComponent as WeChatIcon } from 'src/assets/svg/we-chat-monochrome.svg';
import { ReactComponent as BankTransferIcon } from 'src/assets/svg/bank-transfer-monochrome.svg';
import { ReactComponent as UnionPayIcon } from 'src/assets/svg/union-pay.svg';
import { formatAmount } from 'src/utils/format-amount';
import { useGetAdvanceFeePayment } from 'src/lib/hooks/use-get-advance-fee-payment';
import { transferMateRedirect } from 'src/features/payments/services';
import { routes } from 'src/constants/routes';
import { removeStorage } from 'src/utils/local-storage-versioning';

import { styles } from './styles';

const icons = [
    <TransferMateIcon key="Transfermate" />,
    <VisaIcon key="Visa" />,
    <AmericanExpressIcon key="American Express" />,
    <MastercardIcon key="Mastercard" />,
    <WeChatIcon key="WeChat" />,
    <BankTransferIcon key="Bank Transfer" />,
    <UnionPayIcon key="Union pay" />,
];

export const SuccessPageOria: FC = () => {
    const { t } = useTranslation();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const [isRedirecting, setRedirecting] = useState(false);

    const {
        data: payment,
        isLoading,
        error,
    } = useGetAdvanceFeePayment(searchParams.get('bookingId') ?? '');

    if (error) {
        navigate(routes.failure.index);
        return null;
    }

    if (isLoading || !payment) return null;

    const handlePay = () => {
        window.addEventListener('pagehide', () => setRedirecting(false), { once: true });

        setRedirecting(true);
        transferMateRedirect(payment);
        removeStorage();
    };

    return (
        <Box css={styles.container}>
            <Box css={styles.imageContainer} />

            <Box css={styles.wrapper}>
                <SuccessIcon />

                <Typography variant="h1">{t('success_oria_title')}</Typography>
                <Typography variant="body3">{t('success_oria_subtitle')}</Typography>

                <Box sx={styles.paymentWrapper}>
                    <Typography variant="body1" color="text.secondary">
                        {t('success_oria_panel')}
                    </Typography>

                    <Typography variant="h2" css={styles.paymentAmount}>
                        {formatAmount(payment.amount_due, payment.amount_currency)}
                    </Typography>

                    <Button
                        variant="contained"
                        css={styles.cta}
                        onClick={handlePay}
                        disabled={isRedirecting}
                    >
                        {t('success_oria_cta')}
                    </Button>

                    <Paper elevation={0} css={styles.iconsPanel}>
                        <Typography variant="body2" color="text.secondary">
                            {t('success_oria_info')}
                        </Typography>

                        <Divider css={styles.divider} />

                        <Box sx={styles.iconsGrid}>{icons}</Box>
                    </Paper>
                </Box>
            </Box>
        </Box>
    );
};
